import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ThemeToggle } from '../ThemeToggle';

interface AssessmentLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  showBackButton?: boolean;
  backTo?: string;
  currentStep?: number;
  totalSteps?: number;
  headerActions?: React.ReactNode;
}

export default function AssessmentLayout({
  children,
  title,
  subtitle,
  showBackButton = true,
  backTo,
  currentStep,
  totalSteps = 5,
  headerActions,
}: AssessmentLayoutProps) {
  const navigate = useNavigate();
  const { profile, user } = useAuth();

  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  const progressPercentage = currentStep ? (currentStep / totalSteps) * 100 : 0;

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* White Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {showBackButton && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
                <span className="text-sm font-medium">Tilbake</span>
              </button>
            )}
            {showBackButton && (
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />
            )}
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h1>
              {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {headerActions}
          </div>
        </div>
      </header>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 py-6 pb-12">
          {/* Progress bar */}
          {currentStep && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6 border dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Steg {currentStep} av {totalSteps}: {getStepName(currentStep)}
                </span>
                <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                  {Math.round(progressPercentage)}% fullført
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Main content */}
          {children}
        </div>
      </div>

      {/* Sticky Footer */}
      {currentStep && (
        <div className="bg-white dark:bg-gray-800 px-8 py-4 border-t border-gray-200 dark:border-gray-700 shadow-lg">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Steg {currentStep} av {totalSteps}
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span>Logget inn: {profile?.fullName || user?.email || 'Anonym'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getStepName(step: number): string {
  const steps: Record<number, string> = {
    1: 'Start',
    2: 'Veiledning',
    3: 'Kategorier',
    4: 'Symptomer',
    5: 'Resultat',
  };
  return steps[step] || '';
}
